import { supabase } from '../supabaseClient';
import CONFIG from '../config';
import {
  applyFrontendSearchFilters,
  buildSearchPayload,
  extractChunkHashesFromResults,
  getSearchStyle,
  getChunkRowsFromResult,
  logSearchRankings,
  normalizeSearchOptions,
  normalizeSearchQuery
} from '../helpers/searchHelpers';
import { apiLoadChunksByHashes } from './chunks';
import { debug } from '../helpers/debug';

function normalizeWhitespace(text = '') {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

function isLikelyChatResultRow(row = null) {
  const docType = String(row?.doc_type || '').trim().toLowerCase();
  return docType === 'chat';
}

function levenshteinDistance(left = '', right = '') {
  const a = String(left || '');
  const b = String(right || '');
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const prev = new Array(b.length + 1);
  const curr = new Array(b.length + 1);

  for (let j = 0; j <= b.length; j += 1) prev[j] = j;

  for (let i = 1; i <= a.length; i += 1) {
    curr[0] = i;
    const charA = a.charAt(i - 1);
    for (let j = 1; j <= b.length; j += 1) {
      const cost = charA === b.charAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + cost
      );
    }

    for (let j = 0; j <= b.length; j += 1) prev[j] = curr[j];
  }

  return prev[b.length];
}

function findBestFuzzyQueryMatchIndex(text = '', query = '') {
  const source = String(text || '').toLowerCase();
  const normalizedQuery = normalizeWhitespace(query).toLowerCase();
  if (!source || !normalizedQuery) return null;

  const fuzzyThreshold = Math.max(0.5, Math.min(0.99, Number(CONFIG.SEARCH?.SNIPPET_FUZZY_THRESHOLD || 0.72)));
  const minTermLength = Math.max(3, Number(CONFIG.SEARCH?.SNIPPET_FUZZY_MIN_TERM_LENGTH || 4));

  const queryTerms = normalizedQuery
    .split(/\s+/)
    .map(term => term.trim())
    .filter(term => term.length >= minTermLength)
    .sort((left, right) => right.length - left.length)
    .slice(0, 6);

  if (queryTerms.length === 0) return null;

  const wordRegex = /[a-z0-9]+/gi;
  let tokenMatch;
  let best = null;

  while ((tokenMatch = wordRegex.exec(source)) !== null) {
    const token = String(tokenMatch[0] || '').trim();
    if (!token || token.length < minTermLength) continue;

    for (const term of queryTerms) {
      const maxLen = Math.max(token.length, term.length);
      if (!maxLen) continue;
      const lengthDelta = Math.abs(token.length - term.length);
      const allowedDelta = Math.max(2, Math.floor(maxLen * 0.4));
      if (lengthDelta > allowedDelta) continue;

      const distance = levenshteinDistance(token, term);
      const similarity = 1 - (distance / maxLen);
      if (similarity < fuzzyThreshold) continue;
      if (best && similarity <= best.similarity) continue;

      best = {
        index: tokenMatch.index,
        length: token.length,
        term,
        matchedText: token,
        kind: 'fuzzy',
        similarity: Number(similarity.toFixed(3))
      };
    }
  }

  return best;
}

function findBestQueryMatchIndex(text = '', query = '') {
  const source = String(text || '').toLowerCase();
  const normalizedQuery = normalizeWhitespace(query).toLowerCase();
  if (!source || !normalizedQuery) {
    return { index: -1, length: 0, term: '', kind: null, similarity: null };
  }

  const direct = source.indexOf(normalizedQuery);
  if (direct >= 0) {
    return {
      index: direct,
      length: normalizedQuery.length,
      term: normalizedQuery,
      kind: 'exact',
      similarity: 1
    };
  }

  const terms = normalizedQuery
    .split(/\s+/)
    .map(term => term.trim())
    .filter(term => term.length >= 2)
    .sort((left, right) => right.length - left.length);

  for (const term of terms) {
    const index = source.indexOf(term);
    if (index >= 0) {
      return {
        index,
        length: term.length,
        term,
        kind: 'term',
        similarity: 1
      };
    }
  }

  const fuzzy = findBestFuzzyQueryMatchIndex(source, normalizedQuery);
  if (fuzzy) {
    return {
      index: fuzzy.index,
      length: fuzzy.length,
      term: fuzzy.term,
      kind: fuzzy.kind,
      similarity: fuzzy.similarity,
      matchedText: fuzzy.matchedText
    };
  }

  return { index: -1, length: 0, term: '', kind: null, similarity: null };
}

function buildSnippetFromMarkdown(markdown = '', query = '', options = {}) {
  const source = normalizeWhitespace(markdown);
  if (!source) {
    return {
      snippet: '',
      markedSnippet: '',
      match: null
    };
  }

  const desiredLength = Number(options.contentLength || 260);
  const desiredMatchTarget = Number(options.contentMatchTarget);
  const markerStart = String(options.matchMarkerStart || '[[MATCH_START]]');
  const markerEnd = String(options.matchMarkerEnd || '[[MATCH_END]]');
  const snippetLength = Math.max(60, desiredLength);
  const matchTarget = Number.isFinite(desiredMatchTarget)
    ? Math.max(0.05, Math.min(0.95, desiredMatchTarget))
    : 0.5;
  const match = findBestQueryMatchIndex(source, query);

  let snippetStart = 0;
  if (match.index >= 0 && snippetLength < source.length) {
    const targetOffset = Math.max(0, Math.floor(snippetLength * matchTarget));
    snippetStart = Math.max(0, match.index - targetOffset);
    if (snippetStart + snippetLength > source.length) {
      snippetStart = Math.max(0, source.length - snippetLength);
    }
  }

  const snippetEnd = Math.min(source.length, snippetStart + snippetLength);
  const snippet = source.slice(snippetStart, snippetEnd);

  if (match.index < 0) {
    return {
      snippet,
      markedSnippet: snippet,
      match: null
    };
  }

  const localStart = Math.max(0, match.index - snippetStart);
  const localEnd = Math.min(snippet.length, localStart + match.length);

  const markedSnippet = `${snippet.slice(0, localStart)}${markerStart}${snippet.slice(localStart, localEnd)}${markerEnd}${snippet.slice(localEnd)}`;

  return {
    snippet,
    markedSnippet,
    match: {
      start: localStart,
      end: localEnd,
      globalStart: match.index,
      globalEnd: match.index + match.length,
      term: match.term,
      kind: match.kind || 'term',
      similarity: Number.isFinite(Number(match.similarity)) ? Number(match.similarity) : null
    }
  };
}

async function enrichSearchResultsWithContent(results = [], query = '', options = {}) {
  if (!Array.isArray(results) || results.length === 0) return results;

  const chunkHashes = extractChunkHashesFromResults(results);
  if (chunkHashes.length === 0) {
    return results.map(result => ({
      ...result,
      content_snippet: '',
      content_marked: '',
      content_match: null,
      content_source_chunk_hash: null
    }));
  }

  const chunkRows = await apiLoadChunksByHashes(chunkHashes);
  const chunkByHash = new Map((chunkRows || []).map(row => [String(row?.chunk_hash || ''), row]));

  return results.map((result) => {
    const chunkRows = getChunkRowsFromResult(result);
    const uniqueHashes = Array.from(new Set(
      chunkRows
        .map(row => String(row?.chunk_hash || '').trim())
        .filter(Boolean)
    ));
    const maxSnippets = Math.max(1, Number(options.contentSnippetsPerResult || 3));

    const snippetRows = [];
    for (const sourceHash of uniqueHashes) {
      if (snippetRows.length >= maxSnippets) break;
      const sourceRow = chunkByHash.get(String(sourceHash)) || null;
      const markdown = String(sourceRow?.content_md || '');
      const snippetData = buildSnippetFromMarkdown(markdown, query, options);
      if (!String(snippetData.markedSnippet || snippetData.snippet || '').trim()) continue;

      snippetRows.push({
        chunk_hash: sourceHash,
        content_snippet: snippetData.snippet,
        content_marked: snippetData.markedSnippet,
        content_match: snippetData.match
      });
    }

    const primary = snippetRows[0] || {
      chunk_hash: null,
      content_snippet: '',
      content_marked: '',
      content_match: null
    };

    return {
      ...result,
      content_snippets: snippetRows,
      content_snippet: primary.content_snippet,
      content_marked: primary.content_marked,
      content_match: primary.content_match,
      content_source_chunk_hash: primary.chunk_hash
    };
  });
}

async function enrichSearchResultsWithDocTypes(results = []) {
  if (!Array.isArray(results) || results.length === 0) return results;

  const docIds = Array.from(new Set(
    results
      .map((row) => Number(row?.doc_id || 0))
      .filter((value) => Number.isFinite(value) && value > 0)
  ));

  if (docIds.length === 0) return results;

  try {
    const { data, error } = await supabase
      .from('docs')
      .select('id, type')
      .in('id', docIds);

    if (error) throw error;

    const typeByDocId = new Map(
      (Array.isArray(data) ? data : []).map((row) => [
        Number(row?.id || 0),
        String(row?.type || '').trim().toLowerCase() || null
      ])
    );

    return results.map((row) => {
      const docId = Number(row?.doc_id || 0);
      const docType = typeByDocId.get(docId) || null;
      return {
        ...row,
        doc_type: docType
      };
    });
  } catch (error) {
    debug('[API] enrichSearchResultsWithDocTypes error:', { type: 'warn' }, error);
    return results;
  }
}

export async function apiSearchProject(projectId, query, options = {}) {
  const normalizedQuery = normalizeSearchQuery(query);
  const minLength = Number(CONFIG.SEARCH?.MIN_QUERY_LENGTH || 1);

  if (!projectId || normalizedQuery.length < minLength) {
    const normalizedOptions = normalizeSearchOptions(options);
    return buildSearchPayload({
      projectId,
      query: normalizedQuery,
      options: normalizedOptions,
      rawResults: [],
      filteredResults: []
    });
  }

  const normalizedOptions = normalizeSearchOptions(options);
  const boundedLimit = normalizedOptions.limit;
  const weights = normalizedOptions.weights;
  const rpcName = CONFIG.SEARCH?.RPC_NAME || 'search_project_docs';

  try {
    const { data, error } = await supabase.rpc(rpcName, {
      p_project_id: projectId,
      p_query: normalizedQuery,
      p_limit: boundedLimit,
      p_weights: weights,
      p_result_type: normalizedOptions.resultType,
      p_rank_mode: normalizedOptions.rankMode,
      p_min_score: Number(normalizedOptions.frontendFilter?.minScore || 0),
      p_aggregate_top_k: Number(normalizedOptions.aggregateTopK || 3)
    });

    if (error) throw error;

    const rawResults = Array.isArray(data) ? data : [];
    const filteredResults = normalizedOptions.frontendFiltering
      ? applyFrontendSearchFilters(rawResults, normalizedQuery, normalizedOptions)
      : rawResults;
    const contentEnrichedResults = normalizedOptions.returnContent
      ? await enrichSearchResultsWithContent(filteredResults, normalizedQuery, normalizedOptions)
      : filteredResults;
    const enrichedResults = await enrichSearchResultsWithDocTypes(contentEnrichedResults);
    const finalResults = normalizedOptions.excludeLikelyChatDocs
      ? enrichedResults.filter((row) => !isLikelyChatResultRow(row))
      : enrichedResults;

    logSearchRankings({
      projectId,
      query: normalizedQuery,
      options: normalizedOptions,
      rawResults,
      results: finalResults
    });

    return buildSearchPayload({
      projectId,
      query: normalizedQuery,
      options: normalizedOptions,
      rawResults,
      filteredResults: finalResults
    });
  } catch (err) {
    debug('[API] apiSearchProject error:', { type: 'error' }, err);
    return buildSearchPayload({
      projectId,
      query: normalizedQuery,
      options: normalizedSearchOptionsFallback(options),
      rawResults: [],
      filteredResults: []
    });
  }
}

function normalizedSearchOptionsFallback(options = {}) {
  try {
    return normalizeSearchOptions(options);
  } catch {
    return {
      scope: CONFIG.SEARCH?.DEFAULT_SCOPE || 'project',
      resultType: 'docs',
      limit: Number(CONFIG.SEARCH?.RESULT_LIMIT || 20),
      frontendFiltering: Boolean(CONFIG.SEARCH?.ENABLE_FRONTEND_FILTERING)
    };
  }
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function normalizeDocFromJoinRow(row = {}) {
  const joinedDoc = Array.isArray(row?.docs) ? row.docs[0] : row?.docs;
  const docId = row?.doc_id ?? joinedDoc?.id ?? null;
  const docName = joinedDoc?.name ?? row?.doc_name ?? row?.name ?? '';
  if (docId === null || docId === undefined) return null;

  return {
    result_type: 'docs',
    doc_id: docId,
    doc_name: String(docName || `Document ${docId}`),
    total_score: 1,
    score_details: {
      name_exact: 1,
      name_prefix: 0,
      name_trgm: 0,
      md_exact: 0,
      md_trgm: 0,
      md_fts: 0,
      header_hit: 0,
      top_chunks: []
    }
  };
}

async function searchDocsByExactName(projectId, query, limit = null) {
  const normalizedQuery = normalizeSearchQuery(query);
  const minLength = Number(CONFIG.SEARCH?.MIN_QUERY_LENGTH || 1);
  if (!projectId || normalizedQuery.length < minLength) return [];

  const boundedLimit = Math.max(1, Number(limit || CONFIG.SEARCH?.RESULT_LIMIT || 20));

  const { data, error } = await supabase
    .from('project_docs')
    .select('doc_id, docs!inner(id,name)')
    .eq('project_id', projectId)
    .ilike('docs.name', `${normalizedQuery}%`)
    .limit(boundedLimit);

  if (error) throw error;

  const prefixLower = normalizedQuery.toLowerCase();
  const deduped = new Map();
  for (const row of Array.isArray(data) ? data : []) {
    const normalized = normalizeDocFromJoinRow(row);
    if (!normalized) continue;
    if (!String(normalized.doc_name || '').trim().toLowerCase().startsWith(prefixLower)) continue;
    const key = String(normalized.doc_id);
    if (!deduped.has(key)) deduped.set(key, normalized);
  }

  return Array.from(deduped.values()).slice(0, boundedLimit);
}

export async function apiSearchProjectDocs(projectId, query, limit = null, weightOverridesOrOptions = null) {
  const optionsObject = isPlainObject(weightOverridesOrOptions) && (
    Object.prototype.hasOwnProperty.call(weightOverridesOrOptions, 'style') ||
    Object.prototype.hasOwnProperty.call(weightOverridesOrOptions, 'profile') ||
    Object.prototype.hasOwnProperty.call(weightOverridesOrOptions, 'frontendFilter') ||
    Object.prototype.hasOwnProperty.call(weightOverridesOrOptions, 'weights') ||
    Object.prototype.hasOwnProperty.call(weightOverridesOrOptions, 'scope')
  )
    ? weightOverridesOrOptions
    : {};

  const legacyWeightOverrides = optionsObject === weightOverridesOrOptions
    ? (optionsObject.weights || null)
    : (weightOverridesOrOptions || null);

  const style = getSearchStyle(optionsObject.style);
  const strategy = String(style?.STRATEGY || 'PROFILE').trim().toUpperCase();
  const defaultProfile = String(style?.PROFILE || 'SEARCH_UI').trim().toUpperCase();

  if (strategy === 'DIRECT_DOC_NAME_EXACT') {
    try {
      return await searchDocsByExactName(projectId, query, limit);
    } catch (err) {
      debug('[API] apiSearchProjectDocs exact-name strategy failed, falling back to profile search:', { type: 'warn' }, err);
    }
  }

  const payload = await apiSearchProject(projectId, query, {
    profile: String(optionsObject.profile || defaultProfile).trim().toUpperCase(),
    scope: optionsObject.scope || CONFIG.SEARCH?.DEFAULT_SCOPE || 'project',
    limit,
    weights: optionsObject.weights || legacyWeightOverrides || null,
    frontendFiltering: optionsObject.frontendFiltering,
    frontendFilter: optionsObject.frontendFilter,
    rankMode: optionsObject.rankMode,
    resultType: optionsObject.resultType,
    aggregateTopK: optionsObject.aggregateTopK,
    minScore: optionsObject.minScore,
    returnContent: optionsObject.returnContent
  });

  return Array.isArray(payload?.results) ? payload.results : [];
}
